import { removeFromStorage } from '../../util/storage-utils';
import TokenStorage from './TokenStorage';
import {
  AuthAdapter,
  AuthState,
  SignInWithPasswordMfaRequired,
  SignInWithPasswordResult,
} from './types';
import { createAuthError } from './errorCodes';

class AuthService {
  listeners: { (changes: any): any }[] = [];
  private refreshInterval: NodeJS.Timeout | undefined;
  private lastSignInCredentials:
    | { username: string; password: string }
    | undefined;
  private state: AuthState = {
    isAuthenticated: false,
    isLoading: true,
    error: undefined,
    tokens: undefined,
  };

  constructor(
    private authAdapter: AuthAdapter,
    public tokensStorage: TokenStorage,
  ) {
    try {
      if (!this.tokensStorage.hasTokens() || this.tokensStorage.isExpired()) {
        throw new Error('Sign In required');
      }
      this.setState({
        isLoading: false,
        isAuthenticated: true,
        tokens: this.tokensStorage.get(),
      });
    } catch (error) {
      const contextualError = createAuthError(
        error,
        'AuthService constructor failed',
      );
      this.setState({ isLoading: false, error: contextualError });
      this.signOut();
    }
  }

  getState(): AuthState {
    return this.state;
  }

  startTokenRefresher(): void {
    if (!this.refreshInterval) {
      this.refreshInterval = setInterval(async () => {
        try {
          if (this.tokensStorage.isExpiring()) {
            await this.refreshTokens();
          }
        } catch (_error) {
          this.signOut();
        }
      }, 10000);
    }
  }

  // Currently only used by invoice and/or installer portals. Not used by the main CS secure flow.
  async changePassword(
    email: string,
    oldPassword: string,
    newPassword: string,
  ): Promise<SignInWithPasswordResult> {
    removeFromStorage('isDismissedForSession');
    this.setState({ isLoading: true });
    try {
      const result = await this.authAdapter.changePassword(
        email,
        oldPassword,
        newPassword,
      );
      if (result.status === 'SUCCESS') {
        const { tokens } = result;
        this.tokensStorage.set(tokens);
        this.setState({
          tokens,
          isAuthenticated: true,
          isLoading: false,
          error: undefined,
        });
      } else {
        this.setState({
          isLoading: false,
          error: undefined,
        });
      }

      return result;
    } catch (error) {
      const contextualError = createAuthError(
        error,
        'AuthService.signInWithPassword failed',
      );
      this.setState({ isLoading: false, error: contextualError });
      throw contextualError;
    }
  }

  // Currently only used by invoice and/or installer portals. Not used by the main CS secure flow.
  async forgotPassword(username: string): Promise<void> {
    this.setState({ isLoading: true, isAuthenticated: false });
    try {
      await this.authAdapter.forgotPassword(username);
      this.setState({ isLoading: false, error: undefined });
    } catch (error) {
      const contextualError = createAuthError(
        error,
        'AuthService.forgotPassword failed',
      );
      this.setState({ isLoading: false, error: contextualError });
      throw contextualError;
    }
  }

  // Currently only used by invoice and/or installer portals. Not used by the main CS secure flow.
  async verifyAndResetPassword(
    username: string,
    code: string,
    newPassword: string,
  ): Promise<void> {
    this.setState({ isLoading: true, isAuthenticated: false });
    try {
      await this.authAdapter.verifyAndResetPassword(
        username,
        code,
        newPassword,
      );
      this.setState({ isLoading: false, error: undefined });
    } catch (error) {
      const contextualError = createAuthError(
        error,
        'AuthService.verifyAndResetPassword failed',
      );
      this.setState({ isLoading: false, error: contextualError });
      throw contextualError;
    }
  }

  stopTokenRefresher(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = undefined;
    }
  }

  async signInWithPassword(
    email: string,
    password: string,
  ): Promise<SignInWithPasswordResult> {
    removeFromStorage('isDismissedForSession');
    this.setState({ isLoading: true, error: undefined });
    this.lastSignInCredentials = { username: email, password };
    try {
      const result = await this.authAdapter.signInWithPassword(email, password);

      if (result.status === 'SUCCESS') {
        const { tokens } = result;
        this.tokensStorage.set(tokens);
        this.setState({
          tokens,
          isAuthenticated: true,
          isLoading: false,
          error: undefined,
        });
        this.lastSignInCredentials = undefined;
      } else {
        this.setState({
          isLoading: false,
          error: undefined,
        });
      }

      return result;
    } catch (error) {
      this.lastSignInCredentials = undefined;
      const contextualError = createAuthError(
        error,
        'AuthService.signInWithPassword failed',
      );
      this.setState({ isLoading: false, error: contextualError });
      throw contextualError;
    }
  }

  async signInWithCustomToken(token: string, username: string): Promise<void> {
    if (!username) {
      const contextualError = createAuthError(
        new Error('Username required'),
        'AuthService.signInWithCustomToken failed',
      );
      this.setState({ isLoading: false, error: contextualError });
      throw contextualError;
    }
    // Keep user marked as authenticated while we refresh tokens to avoid route redirects
    this.setState({ isLoading: true });
    try {
      const tokens = await this.authAdapter.signInWithCustomToken(
        token,
        username,
      );
      this.tokensStorage.set(tokens);
      this.setState({
        tokens,
        isAuthenticated: true,
        isLoading: false,
        error: undefined,
      });
    } catch (error) {
      const contextualError = createAuthError(
        error,
        'AuthService.signInWithCustomToken failed',
      );
      this.setState({ isLoading: false, error: contextualError });
      throw contextualError;
    }
  }

  async signInWithMultiFactorAuth(verificationCode: string) {
    removeFromStorage('isDismissedForSession');
    this.setState({ isLoading: true });
    try {
      const tokens = await this.authAdapter.signInWithMultiFactorAuth(
        verificationCode,
      );
      this.tokensStorage.set(tokens);
      this.setState({
        tokens,
        isAuthenticated: true,
        isLoading: false,
        error: undefined,
      });
      this.lastSignInCredentials = undefined;
    } catch (error) {
      const contextualError = createAuthError(
        error,
        'AuthService.signInWithMultiFactorAuth failed',
      );
      this.setState({ isLoading: false, error: contextualError });
      throw contextualError;
    }
  }

  async refreshTokens() {
    const tokens = this.tokensStorage.get();
    try {
      const newTokens = await this.authAdapter.refreshTokens(
        tokens.idToken?.token || '',
      );
      this.tokensStorage.set(newTokens);
      this.setState({ tokens: newTokens, isAuthenticated: true });
    } catch (error) {
      const contextualError = createAuthError(
        error,
        'AuthService.refreshTokens failed',
      );
      this.setState({ isAuthenticated: false, error: contextualError });
      throw contextualError;
    }
  }

  clearLastSignInCredentials(): void {
    this.lastSignInCredentials = undefined;
  }

  async resendMfaCode(options?: {
    clearCachedCredentialsOnFailure?: boolean;
  }): Promise<SignInWithPasswordMfaRequired | void> {
    const { clearCachedCredentialsOnFailure = false } = options || {};

    if (!this.lastSignInCredentials) {
      throw createAuthError(
        new Error('No cached credentials available to resend MFA code'),
        'AuthService.resendMfaCode failed',
      );
    }

    let result: SignInWithPasswordResult;
    try {
      result = await this.authAdapter.signInWithPassword(
        this.lastSignInCredentials.username,
        this.lastSignInCredentials.password,
      );
    } catch (error) {
      if (clearCachedCredentialsOnFailure) {
        this.clearLastSignInCredentials();
      }
      throw createAuthError(error, 'AuthService.resendMfaCode failed');
    }

    if (result.status === 'MFA_REQUIRED') {
      return result;
    }

    if (clearCachedCredentialsOnFailure) {
      this.clearLastSignInCredentials();
    }

    return;
  }

  signOut() {
    this.stopTokenRefresher();
    this.tokensStorage.clear();
    this.lastSignInCredentials = undefined;
    this.setState({ isAuthenticated: false, tokens: undefined });
  }

  private setState(changes: Partial<AuthState>): void {
    this.state = { ...this.state, ...changes };
    this.listeners.forEach(cb => cb(this.state));
  }

  subscribe(cb: (state: AuthState) => any): () => void {
    cb(this.state);
    this.listeners = [...this.listeners, cb];
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== cb);
    };
  }
}
export default AuthService;
